<template>
  <div
    :id="importSource.id"
    :data-navigation="importSource.name"
    class="bg-white rounded-xl border border-slate-200 overflow-hidden"
    :class="{ 'source-highlight': isHighlighted }"
  >
    <div
      class="flex items-center py-4 px-6 cursor-pointer transition-colors group"
      @click="toggleExpanded($event)"
    >
      <div class="flex-1 min-w-0">
        <ImportFromSource
          :import-source="importSource"
          :icon-url="importSource.iconUrl"
          :source-type="sourceType"
        />
      </div>
      <div class="flex items-center flex-shrink-0 gap-6">
        <div class="flex flex-col items-center justify-center w-16">
          <span class="text-base font-bold text-slate-900 leading-none">
            {{ totalResults }}
          </span>
          <span class="text-xs text-slate-400 font-medium">
            {{ totalResults > 1 ? "results" : "result" }}
          </span>
        </div>
        <div class="flex flex-col items-start justify-center w-12">
          <span class="text-base font-bold text-slate-900 leading-none">
            {{ searchCount }}
          </span>
          <span class="text-xs text-slate-400 font-medium">
            {{ searchCount > 1 ? "Queries" : "Query" }}
          </span>
        </div>
        <div class="flex flex-row items-center gap-1">
          <ChevronUpIcon
            v-if="isExpanded"
            class="size-4 text-gray-500 transition-transform duration-200"
          />
          <ChevronRightIcon v-else class="size-4 text-gray-500 transition-transform duration-200" />
          <PlanLockGuard>
            <FloatingMenu>
              <template #activator="{ show }">
                <button
                  v-if="(review.searchesBySource.value[importSource.id]?.length ?? 0) <= 0"
                  class="text-red-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  :disabled="review.isPlanReadonly.value"
                  @click.stop="show"
                >
                  <TrashIcon class="size-4" />
                </button>
                <div v-else class="size-4"></div>
              </template>
              <template #content="{ hide }">
                <div class="p-4 space-y-2">
                  <div>
                    <p>Are you sure you want to delete this source?</p>
                  </div>
                  <div>
                    <button
                      class="bg-neutral-500 hover:bg-neutral-600 text-white py-1 px-2 mr-1"
                      @click.stop="hide"
                    >
                      Cancel
                    </button>
                    <button
                      class="bg-red-500 hover:bg-red-600 text-white py-1 px-2"
                      @click.stop="removeImportSourceFromPlan(importSource.id)"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </template>
            </FloatingMenu>
          </PlanLockGuard>
        </div>
      </div>
    </div>
    <div v-if="isExpanded && isSelected" class="bg-white border-t border-slate-100">
      <div v-if="review.searchesBySource.value[importSource.id]?.length > 0">
        <div
          class="flex items-center text-xs text-slate-900 border-b border-slate-200 bg-neutral-background-secondary py-2 pl-5 font-medium"
        >
          <span class="w-20 flex-shrink-0">Search</span>
          <span class="w-44 flex-shrink-0">Date</span>
          <span
            v-if="importSource.id === BuiltInImportSourceId.CITATION_SEARCH"
            class="w-20 flex-shrink-0"
          >
            Parent
          </span>
          <span
            v-if="
              importSource.id !== BuiltInImportSourceId.HAND_SEARCH &&
              importSource.id !== BuiltInImportSourceId.CITATION_SEARCH
            "
            class="flex-1 min-w-0"
          >
            Query
          </span>
          <span v-else class="flex-1 min-w-0"></span>
          <div class="flex items-center flex-shrink-0">
            <span class="w-20 text-center pr-5">Results</span>
            <span class="w-16"></span>
          </div>
        </div>
        <template
          v-for="(search, index) in getSourceSearches(importSource.id)"
          :key="importSource.id + index"
        >
          <SearchRow :search="search" :search-number="index + 1" :import-source="importSource" />
        </template>
      </div>
      <div v-else class="px-4 py-6 flex flex-col items-center text-slate-500 text-sm">
        <SearchNotFoundIcon />
        <p
          v-if="
            review.entity.value.plan?.lockState !== ReviewLockState.LOCKED &&
            importSource.id === BuiltInImportSourceId.HAND_SEARCH
          "
        >
          You haven't imported any record yet.
        </p>
        <p v-else>You haven't imported any search yet.</p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "@app/views/Review/use-review";
import useLoading from "@app/composables/use-loading";
import useSnackbar from "@app/composables/use-snackbar";
import { SnackbarState } from "@app/types";
import { Search } from "@core/domain/models/search.model";
import TrashIcon from "@app/components/Icons/TrashIcon.vue";
import PlanLockGuard from "@app/views/Review/Plan/PlanLockGuard.vue";
import { HttpException } from "@core/exceptions/http.exception";
import { errorMessage } from "@app/utils/error-message";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";
import { computed } from "vue";
import { ImportSource } from "@core/domain/models/import-source.model";
import { ReviewLockState } from "@core/domain/types/reviewLockState.type";
import SearchRow from "./ImportSourceCard/SearchRow.vue";
import ImportFromSource from "./ImportFromSource.vue";
import SearchNotFoundIcon from "@app/components/Icons/SearchNotFoundIcon.vue";
import ChevronUpIcon from "@app/components/Icons/ChevronUpIcon.vue";
import ChevronRightIcon from "@app/components/Icons/ChevronRightIcon.vue";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";

const props = defineProps<{
  importSource: ImportSource;
  iconUrl?: string;
  query?: string;
  isExpanded?: boolean;
  sourceType?: string;
  isHighlighted?: boolean;
}>();

const loading = useLoading();
const snackbar = useSnackbar();

const review = injectStrict(ReviewKey);

const isSelected = computed(() =>
  review.entity?.value?.plan?.importPlan?.importSources?.some(
    (ri) => ri.id === props.importSource.id,
  ),
);

const searchCount = computed(() => {
  return review.searchesBySource.value[props.importSource.id]?.length ?? 0;
});

const totalResults = computed(() => {
  const searches = review.searchesBySource.value[props.importSource.id] ?? [];
  return searches.reduce((sum, search) => sum + (search.studies?.length ?? 0), 0);
});

const emit = defineEmits<{
  (e: "importSourceRemoved"): void;
  (e: "toggleExpanded", event: MouseEvent): void;
}>();

function toggleExpanded(event: MouseEvent) {
  emit("toggleExpanded", event);
}

async function removeImportSourceFromPlan(sourceId: string) {
  try {
    loading.start();
    emit("importSourceRemoved");
    await review.removeImportSourceFromPlan(sourceId);
    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
  } finally {
    loading.stop();
  }
}

function getSourceSearches(importSourceId: string): Search[] {
  return review.searchesBySource.value[importSourceId] ?? [];
}
</script>
